/* eslint-disable complexity */
import {
  POPULATE_COUNSELOR_DASHBOARD_DISTRICT_INFO,
  UPDATE_COUNSELOR_DASHBOARDS_BUSY_RETRIEVING_STUDENTS,
  POPULATE_COUNSELOR_DASHBOARD_STUDENTS,
  CHANGE_COUNSELOR_DASHBOARD_STUDENTS_PAGE,
  CHANGE_COUNSELOR_DASHBOARD_STUDENTS_PER_PAGE,
  CHANGE_COUNSELOR_DASHBOARD_SORT_MODE,
  TOGGLE_ALL_COUNSELOR_DASHBOARD_STUDENTS_SELECTED,
  TOGGLE_COUNSELOR_DASHBOARD_STUDENT_SELECTED,
  CHANGE_COUNSELOR_DASHBOARD_STUDENT_SEARCH_TERM,
  POPULATE_COUNSELOR_DASHBOARD_STUDENT_NOTE_TAGS,
  POPULATE_COUNSELOR_DASHBOARD_STUDENT_NOTES,
  SET_STUDENT_SELECTED_DASHBOARD,
  SET_STUDENT_UNSELECTED_DASHBOARD,
  RESET_STUDENT_SELECTED_DASHBOARD,
  SET_ACTIVE_DASHBOARD_FILTER,
  SET_SEARCH_TERM,
  POPULATE_COUNSELOR_DASHBOARD_STUDENTS_COUNTS,
  POPULATE_COUNSELOR_DASHBOARD_STUDENTS_HASH,
} from 'constants/counselorDashboard';
import { ensureArray } from 'utils/arrays';
import { ensureObject } from 'utils/objects';

const initialState = {
  activeReportDetails: {
    data: {},
    id: '',
    careers: '',
    segmentTitle: '',
    questionType: '',
    studentList: [],
    clickData: {},
    graphTitle: '',
    studentFilters: {},
  },

  search: null,
  district: {},

  isBusyRetrievingStudents: false,
  students: undefined,
  studentCount: 0,

  isBusyRetrievingGuardiansCount: false,
  guardiansCount: 0,

  page: 1,
  studentsPerPage: 10,
  orderBy: 'last_active',
  order: '-',
  hasNextPage: false,
  hasPrevPage: false,

  // selection state works across pages
  // it changes strategy based on whether 'select all' is checked
  // if it's selected, the 'unchecked' key is being populated
  // otherwise, the 'checked' key gets ids as keys
  // those are stored as objects for faster lookups
  selectedAllStudents: false,
  selectedStudents: {},
  unselectedStudents: {},

  //
  studentSearchTerm: '',

  // student notes
  noteTags: [],
  notesByStudent: {}, // keyed by student id
};

export default function counselorDashboardReducer(
  state = initialState,
  action,
) {
  switch (action.type) {
    case SET_SEARCH_TERM:
      return {
        ...state,
        search: action.search,
      };
    case POPULATE_COUNSELOR_DASHBOARD_DISTRICT_INFO:
      return {
        ...state,
        district: action.district,
      };

    case UPDATE_COUNSELOR_DASHBOARDS_BUSY_RETRIEVING_STUDENTS:
      return {
        ...state,
        isBusyRetrievingStudents: action.mode,
      };
    case POPULATE_COUNSELOR_DASHBOARD_STUDENTS_COUNTS:
      return {
        ...state,
        students: ensureArray(state.students).map(student => ({
          ...student,
          ...ensureObject(
            ensureArray(action.students).find(
              studentWithCounts => studentWithCounts.id === student.id,
            ),
          ),
        })),
      };
    case POPULATE_COUNSELOR_DASHBOARD_STUDENTS_HASH:
      return {
        ...state,
        guardiansCount: action.guardianCount,
        studentCount: action.studentCount,
        studentsHash: action.studentsHash,
      };
    case POPULATE_COUNSELOR_DASHBOARD_STUDENTS:
      return {
        ...state,
        students: action.students || state.students,
        studentCount: action.studentCount,
        hasNextPage: action.hasNextPage || false,
        hasPrevPage: action.hasPrevPage || false,
        selectedStudents: {},
        unselectedStudents: {},
      };

    case CHANGE_COUNSELOR_DASHBOARD_STUDENTS_PAGE:
      return {
        ...state,
        page: action.nextPage || 1,
      };

    case CHANGE_COUNSELOR_DASHBOARD_STUDENTS_PER_PAGE:
      return {
        ...state,
        studentsPerPage: action.howMany || state.studentsPerPage,
      };

    case CHANGE_COUNSELOR_DASHBOARD_SORT_MODE:
      return {
        ...state,
        orderBy: action.fieldName || state.orderBy,
        order: action.direction || state.order,
        selectedStudents: {},
        unselectedStudents: {},
      };

    case TOGGLE_ALL_COUNSELOR_DASHBOARD_STUDENTS_SELECTED: {
      if ([true, false].includes(action.status)) {
        return {
          ...state,
          selectedAllStudents: action.status,
          selectedStudents: {},
          unselectedStudents: {},
        };
      }
      if (!state.selectedAllStudents) {
        return {
          ...state,
          selectedAllStudents: true,
          selectedStudents: {},
          unselectedStudents: {},
        };
      }
      if (
        state.selectedAllStudents &&
        Object.keys(state.unselectedStudents).length
      ) {
        return {
          ...state,
          selectedAllStudents: true,
          unselectedStudents: {},
          selectedStudents: {},
        };
      }

      return {
        ...state,
        selectedAllStudents: false,
        unselectedStudents: {},
        selectedStudents: {},
      };
    }
    case TOGGLE_COUNSELOR_DASHBOARD_STUDENT_SELECTED: {
      if (!state.selectedAllStudents) {
        return {
          ...state,
          selectedStudents: {
            ...state.selectedStudents,
            [action.studentId]: !state.selectedStudents[action.studentId],
          },
        };
      }

      return {
        ...state,
        unselectedStudents: {
          ...state.unselectedStudents,
          [action.studentId]: !state.unselectedStudents[action.studentId],
        },
      };
    }

    case SET_STUDENT_SELECTED_DASHBOARD: {
      return {
        ...state,
        selectedStudents: {
          ...state.selectedStudents,
          [action.studentId]: true,
        },
      };
    }

    case SET_STUDENT_UNSELECTED_DASHBOARD: {
      return {
        ...state,
        selectedStudents: {
          ...state.selectedStudents,
          [action.studentId]: false,
        },
      };
    }

    case RESET_STUDENT_SELECTED_DASHBOARD: {
      return {
        ...state,
        search: null,
        selectedStudents: {},
        unselectedStudents: {},
        selectedAllStudents: false,
      };
    }

    case 'CLEAR_ALL_ACTIVE_REPORT_DETAILS': {
      return {
        ...state,
        activeReportDetails: {
          ...state.activeReportDetails,
          studentList: [],
          clickData: {},
          graphTitle: '',
          studentsHash: '',
          studentScope: null,
        },
      };
    }

    case CHANGE_COUNSELOR_DASHBOARD_STUDENT_SEARCH_TERM:
      return {
        ...state,
        studentSearchTerm: action.nextTerm || '',
        search: action.nextTerm || '',
      };

    case POPULATE_COUNSELOR_DASHBOARD_STUDENT_NOTE_TAGS:
      return {
        ...state,
        noteTags: action.noteTags,
      };

    case POPULATE_COUNSELOR_DASHBOARD_STUDENT_NOTES:
      return {
        ...state,
        notesByStudent: {
          ...state.notesByStudent,
          [action.studentId]: action.notes,
        },
      };
    case SET_ACTIVE_DASHBOARD_FILTER:
      return {
        ...state,
        activeReportDetails: {
          ...state.activeReportDetails,
          id: action.data.id,
          studentList: action.data.studentsHash
            ? null
            : action.data.studentList,
          sectionName: action.data.sectionName,
          studentsHash: action.data.studentsHash,
          studentScope: action.data?.studentScope || null,
          segmentTitle: action.data.segmentTitle,
          questionType: action.data.questionType,
          graphTitle: action.data.graphTitle,
          studentFilters: ensureObject(action.data.studentFilters),
        },
      };

    default:
      return state;
  }
}
